import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { FormField, Inline, Paragraph, SegmentedMenu } from '@george-labs.com/design-system';
import type { SegmentedMenuItem } from '@george-labs.com/design-system/types/components/SegmentedMenu/SegmentedMenu';
import type { FORM_FIELD_LABEL_POSITION } from '@george-labs.com/design-system/types/components/Form';

import type { FormFieldProps } from '@shared/form2';
import { shouldShowError } from '@shared/form2';
import { useField } from '@luna/form2';

import styles from './FormSegmentedButtonGroupField.module.scss';

type SegmentedMenuItemWithDataCy = Omit<SegmentedMenuItem, 'label'> & {
    label: ReactNode;
    'data-cy'?: string;
};

export type SegmentedButtonGroupFieldOption<FieldValue extends string = string> =
    SegmentedMenuItemWithDataCy & {
        key: FieldValue;
    };

interface Props<FormValues, FieldValue extends string> extends FormFieldProps<FormValues> {
    items: SegmentedButtonGroupFieldOption<FieldValue>[];
    className?: string;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

interface FormSegmentedButtonGroupFieldStatics {
    LABEL_POSITION: typeof FORM_FIELD_LABEL_POSITION;
}

const LABEL_COLUMN_WIDTH = 4;

// GDS SegmentedMenuItem defines label as string but a ReactNode works as well.
// There is currently no other way to add data-cy to individual buttons.
const wrapItemWithDataCy = ({
    'data-cy': dataCy,
    label,
    ...item
}: SegmentedButtonGroupFieldOption): SegmentedButtonGroupFieldOption => ({
    ...item,
    label: <Inline data-cy={dataCy}>{label}</Inline>,
});

export function FormSegmentedButtonGroupField<
    FormValues = void,
    FieldValue extends string = string,
>({
    'data-cy': dataCy,
    fieldName,
    items,
    label,
    labelPosition,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    required = false,
    disabled = false,
    description,
    className,
    isErrorVisible = true,
    warning,
    info,
    addon,
    shouldClearOnUnmount,
}: Props<FormValues, FieldValue>) {
    const [{ value }, { error, touched }, { setTouched, setValue }] = useField<FieldValue>(
        fieldName,
        { shouldClearOnUnmount },
    );

    const isErrorMessageVisible = shouldShowError(isErrorVisible, touched, error);

    const itemsWithDataCy = useMemo(() => items.map(wrapItemWithDataCy), [items]);
    const selected = useMemo(
        () => itemsWithDataCy.find(({ key }) => key === value),
        [itemsWithDataCy, value],
    );

    const handleChange = useCallback(
        ({ key }: SegmentedButtonGroupFieldOption<FieldValue>): void => {
            setValue(key);
            setTouched();
        },
        [setTouched, setValue],
    );

    return (
        <FormField
            description={description}
            label={label && <Inline data-cy={`${dataCy}-label`}>{label}</Inline>}
            labelPosition={labelPosition}
            labelColumnWidth={labelColumnWidth}
            required={required}
            disabled={disabled}
            error={
                isErrorMessageVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            className={className}
            name={fieldName}
            addon={addon}
            warning={warning}
            info={info}
            input={
                <SegmentedMenu
                    data-cy={dataCy}
                    segmentedButtonGroupClassName={styles.buttonGroup}
                    items={itemsWithDataCy as SegmentedMenuItem[]}
                    selected={selected as SegmentedMenuItem}
                    // Conflicting type definition in GDS SegmentedMenu
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onSelect={handleChange as any}
                />
            }
        />
    );
}

export type FormSegmentedButtonGroupField = typeof FormSegmentedButtonGroupField &
    FormSegmentedButtonGroupFieldStatics;

FormSegmentedButtonGroupField.LABEL_POSITION = FormField.LABEL_POSITION;
